import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle } from "lucide-react";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", cover_letter: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let resume_file_id = null;
      if (resume) {
        const fd = new FormData();
        fd.append("file", resume);
        const up = await api.post("/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_file_id = up.data.file_id;
      }
      await api.post("/job-applications", { ...form, resume_file_id });
      setDone(true);
      toast.success("Application submitted!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24 text-center" data-testid="careers-success">
        <CheckCircle className="mx-auto text-[var(--ff-green)] mb-6" size={64} />
        <h1 className="font-display text-5xl mb-4">APPLICATION RECEIVED.</h1>
        <p className="text-[var(--ff-text-dim)]">Thank you for applying to Freshy Fresh Productions. Our HR team will review your submission and respond within 5–10 business days.</p>
      </div>
    );
  }

  return (
    <div data-testid="careers-page" className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24">
      <div className="label-eyebrow mb-6 text-[var(--ff-red)]">▮ CAREERS / OPEN ROLES</div>
      <h1 className="font-display text-5xl lg:text-7xl mb-4">JOIN THE NETWORK.</h1>
      <p className="text-[var(--ff-text-dim)] text-lg max-w-3xl mb-16">
        Production, post, marketing, music, broadcast operations, and creative direction. Submit your application below.
      </p>

      <form onSubmit={submit} className="grid md:grid-cols-2 gap-6 surface-card p-8 lg:p-12" data-testid="career-form">
        <div>
          <label className="label-eyebrow block mb-2">Full Name</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3" data-testid="career-name-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Email</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3" data-testid="career-email-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Phone</label>
          <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3" data-testid="career-phone-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Position Applying For</label>
          <input required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full px-4 py-3" data-testid="career-position-input" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-2">Cover Letter</label>
          <textarea required value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} rows={6} className="w-full px-4 py-3" data-testid="career-cover-letter" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-2">Resume (PDF / DOC / DOCX)</label>
          <label className="border-2 border-dashed border-[var(--ff-border)] hover:border-[var(--ff-red)] transition-colors p-8 cursor-pointer flex flex-col items-center justify-center text-center" data-testid="resume-upload-zone">
            <Upload size={28} className="text-[var(--ff-text-dim)] mb-3" />
            <span className="font-mono-ui text-xs uppercase tracking-[0.2em]">
              {resume ? resume.name : "Click to upload resume"}
            </span>
            <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={(e) => setResume(e.target.files?.[0] || null)} className="hidden" data-testid="resume-file-input" />
          </label>
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button disabled={submitting} className="btn-primary" data-testid="career-submit-btn">
            {submitting ? "Submitting..." : "Submit Application →"}
          </button>
        </div>
      </form>
    </div>
  );
}
